/**
 * MasterComposerTab.tsx
 *
 * Tab 2: "Master BOM" — always-on inline cell editing (ProductionOrderGrid pattern).
 * Spec: docs/superpowers/plans/2026-09-22-bom-master-composer-spec.md
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  AlertTriangle,
  Plus,
  MoreVertical,
  Copy,
  Trash2,
  Layers,
  Box,
  SlidersHorizontal,
} from 'lucide-react'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Typeahead } from '@/components/forms/Typeahead'
import type {
  BomMasterWoodLine,
  BomMasterHardwareLine,
  ApplicableAssemblyOption,
} from '@/services/boms/masterTypes'

type PreviewColorOption = { code: string; name: string }
type PreviewAssemblyOption = { code: string; name: string }

const PREVIEW_ASSEMBLY_OPTIONS: PreviewAssemblyOption[] = [
  { code: 'ALL', name: '— Tất cả kết cấu —' },
  { code: 'assembled', name: 'Lắp ráp (assembled)' },
  { code: 'kd', name: 'Tháo rời (kd)' },
]

type LinePatch = Partial<BomMasterWoodLine> & Partial<BomMasterHardwareLine>

interface MasterComposerTabProps {
  woodLines: BomMasterWoodLine[]
  hardwareLines: BomMasterHardwareLine[]
  woodDuplicateCount: number
  hardwareDuplicateCount: number
  availableColors: Array<{ code: string; name: string }>
  onAddLine: (type: 'wood' | 'hardware') => void
  onUpdateLine: (
    id: string,
    type: 'wood' | 'hardware',
    patch: LinePatch,
  ) => void | Promise<void>
  onDuplicateLine: (id: string, type: 'wood' | 'hardware') => void
  onDeleteLine: (id: string, type: 'wood' | 'hardware') => void
  onOpenScopePicker: (
    line: BomMasterWoodLine | BomMasterHardwareLine,
    type: 'wood' | 'hardware',
  ) => void
  previewColor: string
  previewAssembly: string
  onChangePreviewColor: (color: string) => void
  onChangePreviewAssembly: (assembly: string) => void
}

const inputClass =
  'w-full rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-800 outline-none focus:border-sky-500'
const dimInputClass =
  'w-12 rounded border border-slate-300 bg-white px-1 py-1 text-xs text-right font-mono outline-none focus:border-sky-500'
const thClass =
  'py-2.5 px-3 border border-slate-300 bg-slate-50 font-semibold text-slate-900'
const tdClass = 'py-2 px-3 border border-slate-300 bg-white text-slate-900'

function InlineCell({
  value,
  onCommit,
  type = 'text',
  placeholder,
  className = inputClass,
  min,
  step,
  emptyAsNull = false,
}: {
  value: string | number | null | undefined
  onCommit: (next: string | number | null) => void
  type?: 'text' | 'number'
  placeholder?: string
  className?: string
  min?: number
  step?: string
  /** When true, empty string commits as null (for optional text/number fields). */
  emptyAsNull?: boolean
}) {
  const display =
    value === null || value === undefined ? '' : String(value)
  const [local, setLocal] = useState(display)

  useEffect(() => {
    setLocal(display)
  }, [display])

  const commit = () => {
    if (local === display) return
    if (type === 'number') {
      if (local.trim() === '') {
        onCommit(emptyAsNull ? null : 0)
        return
      }
      const n = Number(local)
      if (Number.isNaN(n)) {
        setLocal(display)
        return
      }
      onCommit(n)
      return
    }
    if (local.trim() === '' && emptyAsNull) {
      onCommit(null)
      return
    }
    onCommit(local)
  }

  return (
    <input
      type={type}
      value={local}
      placeholder={placeholder}
      min={min}
      step={step}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={commit}
      className={className}
    />
  )
}

function RowActionMenu({
  onDuplicate,
  onDelete,
}: {
  onDuplicate: () => void
  onDelete: () => void
}) {
  const [open, setOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open])

  return (
    <div className="relative inline-block text-left" ref={menuRef}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-label="Menu thao tác dòng"
        className="p-1 rounded text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
      >
        <MoreVertical className="h-4 w-4" />
      </button>

      {open && (
        <div className="absolute right-0 z-20 mt-1 w-36 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none border border-slate-300">
          <button
            type="button"
            onClick={() => {
              setOpen(false)
              onDuplicate()
            }}
            className="flex w-full items-center px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50"
          >
            <Copy className="mr-2 h-3.5 w-3.5 text-slate-400" />
            Nhân đôi dòng
          </button>
          <div className="my-1 border-t border-slate-200" />
          <button
            type="button"
            onClick={() => {
              setOpen(false)
              if (window.confirm('Bạn có chắc chắn muốn xóa dòng này không?')) {
                onDelete()
              }
            }}
            className="flex w-full items-center px-3 py-1.5 text-xs text-rose-600 hover:bg-rose-50"
          >
            <Trash2 className="mr-2 h-3.5 w-3.5 text-rose-500" />
            Xóa dòng
          </button>
        </div>
      )}
    </div>
  )
}

function renderColorChips(colors: string[], onClick: () => void) {
  const isAll = colors.includes('ALL')
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex flex-wrap items-center gap-1 text-left group hover:opacity-80 transition-opacity"
      title="Bấm để sửa màu áp dụng"
    >
      {isAll ? (
        <Badge tone="neutral">TẤT CẢ</Badge>
      ) : (
        colors.map((c) => (
          <Badge key={c} tone="neutral" className="font-mono">
            {c}
          </Badge>
        ))
      )}
    </button>
  )
}

function renderAssemblyChips(
  assemblies: ApplicableAssemblyOption[],
  onClick: () => void,
) {
  const isAll = assemblies.includes('ALL')
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex flex-wrap items-center gap-1 text-left group hover:opacity-80 transition-opacity"
      title="Bấm để sửa kết cấu áp dụng"
    >
      {isAll ? (
        <Badge tone="neutral">TẤT CẢ</Badge>
      ) : (
        assemblies.map((a) => (
          <Badge key={a} tone={a === 'assembled' ? 'info' : 'warning'}>
            {a === 'assembled' ? 'Lắp ráp' : 'KD'}
          </Badge>
        ))
      )}
    </button>
  )
}

export function MasterComposerTab({
  woodLines,
  hardwareLines,
  woodDuplicateCount,
  hardwareDuplicateCount,
  availableColors,
  onAddLine,
  onUpdateLine,
  onDuplicateLine,
  onDeleteLine,
  onOpenScopePicker,
  previewColor,
  previewAssembly,
  onChangePreviewColor,
  onChangePreviewAssembly,
}: MasterComposerTabProps) {
  const [onlyApplicable, setOnlyApplicable] = useState(false)

  const hasDuplicates = woodDuplicateCount > 0 || hardwareDuplicateCount > 0

  const previewColorOptions: PreviewColorOption[] = useMemo(() => {
    return [
      { code: 'ALL', name: '— Tất cả màu —' },
      ...availableColors.map((c) => ({
        code: c.code,
        name: c.name ? `${c.code} (${c.name})` : c.code,
      })),
    ]
  }, [availableColors])

  const isLineApplicable = (
    applicableColors: string[],
    applicableAssembly: string[],
  ): boolean => {
    const colorMatch =
      previewColor === 'ALL' ||
      applicableColors.includes('ALL') ||
      applicableColors.includes(previewColor)

    const assemblyMatch =
      previewAssembly === 'ALL' ||
      applicableAssembly.includes('ALL') ||
      applicableAssembly.includes(previewAssembly)

    return colorMatch && assemblyMatch
  }

  return (
    <div className="space-y-4">
      {/* Duplicate 100% warning banner */}
      {hasDuplicates && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-xs text-amber-900 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="font-bold text-sm text-amber-800">
              Cảnh báo trùng dòng 100%
            </h4>
            <p className="mt-0.5 text-amber-700">
              Phát hiện{' '}
              <strong>
                {woodDuplicateCount > 0 ? `${woodDuplicateCount} dòng gỗ` : ''}
                {woodDuplicateCount > 0 && hardwareDuplicateCount > 0 ? ' và ' : ''}
                {hardwareDuplicateCount > 0
                  ? `${hardwareDuplicateCount} dòng ngũ kim`
                  : ''}
              </strong>{' '}
              có tên, thông số kỹ thuật và phạm vi áp dụng (Màu, Kết cấu) giống hệt nhau
              100%. Vui lòng rà soát (hệ thống không tự xóa hoặc chặn lưu).
            </p>
          </div>
        </div>
      )}

      {/* Preview filters */}
      <div className="bg-white p-3.5 rounded-lg border border-slate-300 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1.5 text-slate-700 font-semibold text-xs">
          <SlidersHorizontal className="h-3.5 w-3.5 text-slate-500" />
          <span>Xem trước theo điều kiện:</span>
        </div>

        <div className="flex items-center gap-1.5 min-w-[200px]">
          <label
            htmlFor="preview-color-select"
            className="text-xs text-slate-500 shrink-0"
          >
            Màu:
          </label>
          <Typeahead<PreviewColorOption>
            id="preview-color-select"
            items={previewColorOptions}
            value={
              previewColorOptions.find((c) => c.code === previewColor) ||
              previewColorOptions[0] ||
              null
            }
            onChange={(item) => onChangePreviewColor(item?.code || 'ALL')}
            displayValue={(c) =>
              c.code === 'ALL' ? c.name : `${c.code} (${c.name})`
            }
            placeholder="Tìm màu..."
            clearable={false}
            inputClassName="h-8 text-xs bg-white"
            aria-label="Xem trước theo màu"
          />
        </div>

        <div className="flex items-center gap-1.5 min-w-[200px]">
          <label
            htmlFor="preview-assembly-select"
            className="text-xs text-slate-500 shrink-0"
          >
            Kết cấu:
          </label>
          <Typeahead<PreviewAssemblyOption>
            id="preview-assembly-select"
            items={PREVIEW_ASSEMBLY_OPTIONS}
            value={
              PREVIEW_ASSEMBLY_OPTIONS.find((a) => a.code === previewAssembly) ||
              PREVIEW_ASSEMBLY_OPTIONS[0]
            }
            onChange={(item) => onChangePreviewAssembly(item?.code || 'ALL')}
            displayValue={(a) => a.name}
            placeholder="Chọn kết cấu..."
            clearable={false}
            inputClassName="h-8 text-xs bg-white"
            aria-label="Xem trước theo kết cấu"
          />
        </div>

        <label className="flex items-center gap-1.5 text-xs text-slate-700 cursor-pointer select-none ml-2">
          <input
            type="checkbox"
            checked={onlyApplicable}
            onChange={(e) => setOnlyApplicable(e.target.checked)}
            className="rounded border-slate-300 text-slate-700 focus:ring-slate-400"
          />
          <span>Chỉ hiện dòng áp dụng</span>
        </label>
      </div>

      {/* Wood table */}
      <div className="bg-white rounded-lg border border-slate-300 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-300 flex flex-wrap items-center justify-between gap-3 bg-slate-50">
          <div className="flex items-center gap-2">
            <Layers className="h-4 w-4 text-slate-600" />
            <h3 className="text-sm font-bold text-slate-900">
              Chi tiết Gỗ (Wood Parts)
            </h3>
            <Badge tone="neutral">{woodLines.length} dòng</Badge>
            {woodDuplicateCount > 0 && (
              <Badge tone="warning">
                <AlertTriangle className="h-3 w-3" />
                {woodDuplicateCount} trùng
              </Badge>
            )}
          </div>

          <Button
            variant="primary"
            onClick={() => onAddLine('wood')}
            className="h-8 px-3 text-xs flex items-center gap-1"
          >
            <Plus className="h-3.5 w-3.5" />
            <span>+ Thêm dòng gỗ</span>
          </Button>
        </div>

        {woodLines.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-xs">
            Chưa có dòng gỗ nào trong Master BOM. Bấm &quot;+ Thêm dòng gỗ&quot; hoặc
            &quot;Nhập từ BOM cũ&quot; để bắt đầu.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left border-collapse text-slate-900">
              <thead>
                <tr>
                  <th className={`${thClass} w-10 text-center`}>STT</th>
                  <th className={`${thClass} min-w-[180px]`}>Tên chi tiết</th>
                  <th className={`${thClass} min-w-[110px]`}>Cụm</th>
                  <th className={`${thClass} min-w-[110px]`}>Loại gỗ</th>
                  <th className={`${thClass} min-w-[170px] text-center`}>
                    KT Thành phẩm (D×R×D mm)
                  </th>
                  <th className={`${thClass} min-w-[170px] text-center`}>
                    KT Thô (D×R×D mm)
                  </th>
                  <th className={`${thClass} min-w-[90px] text-center`}>Mộng</th>
                  <th className={`${thClass} w-16 text-right`}>SL/SP</th>
                  <th className={`${thClass} min-w-[100px]`}>Veneer</th>
                  <th className={`${thClass} min-w-[120px]`}>Ghi chú</th>
                  <th className={`${thClass} min-w-[110px]`}>Màu áp dụng</th>
                  <th className={`${thClass} min-w-[100px]`}>Kết cấu áp dụng</th>
                  <th className={`${thClass} w-12 text-center`}></th>
                </tr>
              </thead>
              <tbody>
                {woodLines.map((line, index) => {
                  const applicable = isLineApplicable(
                    line.applicable_colors,
                    line.applicable_assembly,
                  )
                  if (onlyApplicable && !applicable) return null

                  const isDup = Boolean(line.is_duplicate)
                  const dupTd = isDup ? 'bg-amber-50/40' : ''

                  return (
                    <tr
                      key={line.id}
                      className={`transition-colors ${
                        !applicable
                          ? 'opacity-40'
                          : isDup
                            ? 'bg-amber-50/40 hover:bg-amber-50/70'
                            : 'hover:bg-slate-50'
                      }`}
                    >
                      <td
                        className={`${tdClass} text-center font-mono text-slate-500 ${dupTd}`}
                      >
                        {index + 1}
                      </td>

                      <td className={`${tdClass} ${dupTd}`}>
                        <div className="flex items-center gap-1">
                          <InlineCell
                            value={line.part_name}
                            placeholder="Tên chi tiết..."
                            onCommit={(v) =>
                              void onUpdateLine(line.id, 'wood', {
                                part_name: String(v ?? ''),
                              })
                            }
                          />
                          {isDup && (
                            <span
                              title="Dòng này có nội dung và phạm vi áp dụng trùng 100% với một dòng khác"
                              className="text-amber-500 inline-flex shrink-0"
                            >
                              <AlertTriangle className="h-3.5 w-3.5" />
                            </span>
                          )}
                        </div>
                      </td>

                      <td className={`${tdClass} ${dupTd}`}>
                        <InlineCell
                          value={line.part_group}
                          placeholder="Khung, Chân..."
                          emptyAsNull
                          onCommit={(v) =>
                            void onUpdateLine(line.id, 'wood', {
                              part_group: v === null ? null : String(v),
                            })
                          }
                        />
                      </td>

                      <td className={`${tdClass} ${dupTd}`}>
                        <InlineCell
                          value={line.wood_type_raw}
                          placeholder="Sồi, Cao su..."
                          emptyAsNull
                          onCommit={(v) =>
                            void onUpdateLine(line.id, 'wood', {
                              wood_type_raw: v === null ? null : String(v),
                            })
                          }
                        />
                      </td>

                      <td className={`${tdClass} text-center ${dupTd}`}>
                        <div className="flex items-center justify-center gap-1">
                          <InlineCell
                            type="number"
                            step="any"
                            value={line.dims_finished_t}
                            placeholder="T"
                            className={dimInputClass}
                            emptyAsNull
                            onCommit={(v) =>
                              void onUpdateLine(line.id, 'wood', {
                                dims_finished_t:
                                  typeof v === 'number' ? v : null,
                              })
                            }
                          />
                          <span>×</span>
                          <InlineCell
                            type="number"
                            step="any"
                            value={line.dims_finished_w}
                            placeholder="W"
                            className={dimInputClass}
                            emptyAsNull
                            onCommit={(v) =>
                              void onUpdateLine(line.id, 'wood', {
                                dims_finished_w:
                                  typeof v === 'number' ? v : null,
                              })
                            }
                          />
                          <span>×</span>
                          <InlineCell
                            type="number"
                            step="any"
                            value={line.dims_finished_l}
                            placeholder="L"
                            className={dimInputClass}
                            emptyAsNull
                            onCommit={(v) =>
                              void onUpdateLine(line.id, 'wood', {
                                dims_finished_l:
                                  typeof v === 'number' ? v : null,
                              })
                            }
                          />
                        </div>
                      </td>

                      <td className={`${tdClass} text-center ${dupTd}`}>
                        <div className="flex items-center justify-center gap-1">
                          <InlineCell
                            type="number"
                            step="any"
                            value={line.dims_rough_t}
                            placeholder="T"
                            className={`${dimInputClass} text-slate-600`}
                            emptyAsNull
                            onCommit={(v) =>
                              void onUpdateLine(line.id, 'wood', {
                                dims_rough_t: typeof v === 'number' ? v : null,
                              })
                            }
                          />
                          <span>×</span>
                          <InlineCell
                            type="number"
                            step="any"
                            value={line.dims_rough_w}
                            placeholder="W"
                            className={`${dimInputClass} text-slate-600`}
                            emptyAsNull
                            onCommit={(v) =>
                              void onUpdateLine(line.id, 'wood', {
                                dims_rough_w: typeof v === 'number' ? v : null,
                              })
                            }
                          />
                          <span>×</span>
                          <InlineCell
                            type="number"
                            step="any"
                            value={line.dims_rough_l}
                            placeholder="L"
                            className={`${dimInputClass} text-slate-600`}
                            emptyAsNull
                            onCommit={(v) =>
                              void onUpdateLine(line.id, 'wood', {
                                dims_rough_l: typeof v === 'number' ? v : null,
                              })
                            }
                          />
                        </div>
                      </td>

                      <td className={`${tdClass} text-center ${dupTd}`}>
                        <InlineCell
                          value={line.tenon}
                          placeholder="Mộng..."
                          className={`${inputClass} text-center`}
                          emptyAsNull
                          onCommit={(v) =>
                            void onUpdateLine(line.id, 'wood', {
                              tenon: v === null ? null : String(v),
                            })
                          }
                        />
                      </td>

                      <td className={`${tdClass} text-right ${dupTd}`}>
                        <InlineCell
                          type="number"
                          step="any"
                          min={0.001}
                          value={line.qty_per}
                          className={`${inputClass} w-14 text-right font-mono font-bold`}
                          onCommit={(v) =>
                            void onUpdateLine(line.id, 'wood', {
                              qty_per: typeof v === 'number' && v > 0 ? v : 1,
                            })
                          }
                        />
                      </td>

                      <td className={`${tdClass} ${dupTd}`}>
                        <InlineCell
                          value={line.veneer_spec}
                          placeholder="Veneer..."
                          emptyAsNull
                          onCommit={(v) =>
                            void onUpdateLine(line.id, 'wood', {
                              veneer_spec: v === null ? null : String(v),
                            })
                          }
                        />
                      </td>

                      <td className={`${tdClass} ${dupTd}`}>
                        <InlineCell
                          value={line.notes}
                          placeholder="Ghi chú..."
                          emptyAsNull
                          onCommit={(v) =>
                            void onUpdateLine(line.id, 'wood', {
                              notes: v === null ? null : String(v),
                            })
                          }
                        />
                      </td>

                      <td className={`${tdClass} ${dupTd}`}>
                        {renderColorChips(line.applicable_colors, () =>
                          onOpenScopePicker(line, 'wood'),
                        )}
                      </td>

                      <td className={`${tdClass} ${dupTd}`}>
                        {renderAssemblyChips(line.applicable_assembly, () =>
                          onOpenScopePicker(line, 'wood'),
                        )}
                      </td>

                      <td className={`${tdClass} text-center ${dupTd}`}>
                        <RowActionMenu
                          onDuplicate={() => onDuplicateLine(line.id, 'wood')}
                          onDelete={() => onDeleteLine(line.id, 'wood')}
                        />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Hardware table */}
      <div className="bg-white rounded-lg border border-slate-300 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-300 flex flex-wrap items-center justify-between gap-3 bg-slate-50">
          <div className="flex items-center gap-2">
            <Box className="h-4 w-4 text-slate-600" />
            <h3 className="text-sm font-bold text-slate-900">
              Ngũ kim & Phụ kiện (Hardware Lines)
            </h3>
            <Badge tone="neutral">{hardwareLines.length} dòng</Badge>
            {hardwareDuplicateCount > 0 && (
              <Badge tone="warning">
                <AlertTriangle className="h-3 w-3" />
                {hardwareDuplicateCount} trùng
              </Badge>
            )}
          </div>

          <Button
            variant="primary"
            onClick={() => onAddLine('hardware')}
            className="h-8 px-3 text-xs flex items-center gap-1"
          >
            <Plus className="h-3.5 w-3.5" />
            <span>+ Thêm ngũ kim</span>
          </Button>
        </div>

        {hardwareLines.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-xs">
            Chưa có dòng ngũ kim nào trong Master BOM. Bấm &quot;+ Thêm ngũ kim&quot;
            hoặc &quot;Nhập từ BOM cũ&quot; để bắt đầu.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left border-collapse text-slate-900">
              <thead>
                <tr>
                  <th className={`${thClass} w-10 text-center`}>STT</th>
                  <th className={`${thClass} min-w-[200px]`}>
                    Tên vật tư / Ngũ kim
                  </th>
                  <th className={`${thClass} min-w-[140px]`}>
                    Quy cách / Thông số
                  </th>
                  <th className={`${thClass} w-20 text-center`}>ĐVT</th>
                  <th className={`${thClass} w-16 text-right`}>SL/SP</th>
                  <th className={`${thClass} min-w-[100px]`}>Gợi ý màu</th>
                  <th className={`${thClass} min-w-[140px]`}>Ghi chú</th>
                  <th className={`${thClass} min-w-[110px]`}>Màu áp dụng</th>
                  <th className={`${thClass} min-w-[100px]`}>Kết cấu áp dụng</th>
                  <th className={`${thClass} w-12 text-center`}></th>
                </tr>
              </thead>
              <tbody>
                {hardwareLines.map((line, index) => {
                  const applicable = isLineApplicable(
                    line.applicable_colors,
                    line.applicable_assembly,
                  )
                  if (onlyApplicable && !applicable) return null

                  const isDup = Boolean(line.is_duplicate)
                  const dupTd = isDup ? 'bg-amber-50/40' : ''

                  return (
                    <tr
                      key={line.id}
                      className={`transition-colors ${
                        !applicable
                          ? 'opacity-40'
                          : isDup
                            ? 'bg-amber-50/40 hover:bg-amber-50/70'
                            : 'hover:bg-slate-50'
                      }`}
                    >
                      <td
                        className={`${tdClass} text-center font-mono text-slate-500 ${dupTd}`}
                      >
                        {index + 1}
                      </td>

                      <td className={`${tdClass} ${dupTd}`}>
                        <div className="flex items-center gap-1">
                          <InlineCell
                            value={line.name_raw}
                            placeholder="Tên ngũ kim..."
                            onCommit={(v) =>
                              void onUpdateLine(line.id, 'hardware', {
                                name_raw: String(v ?? ''),
                              })
                            }
                          />
                          {isDup && (
                            <span
                              title="Dòng này có nội dung và phạm vi áp dụng trùng 100% với một dòng khác"
                              className="text-amber-500 inline-flex shrink-0"
                            >
                              <AlertTriangle className="h-3.5 w-3.5" />
                            </span>
                          )}
                        </div>
                      </td>

                      <td className={`${tdClass} ${dupTd}`}>
                        <InlineCell
                          value={line.spec_raw}
                          placeholder="Quy cách..."
                          emptyAsNull
                          onCommit={(v) =>
                            void onUpdateLine(line.id, 'hardware', {
                              spec_raw: v === null ? null : String(v),
                            })
                          }
                        />
                      </td>

                      <td className={`${tdClass} text-center ${dupTd}`}>
                        <InlineCell
                          value={line.uom_hint}
                          placeholder="Cái"
                          className={`${inputClass} w-16 text-center`}
                          emptyAsNull
                          onCommit={(v) =>
                            void onUpdateLine(line.id, 'hardware', {
                              uom_hint: v === null ? null : String(v),
                            })
                          }
                        />
                      </td>

                      <td className={`${tdClass} text-right ${dupTd}`}>
                        <InlineCell
                          type="number"
                          step="any"
                          min={0.001}
                          value={line.qty_per}
                          className={`${inputClass} w-14 text-right font-mono font-bold`}
                          onCommit={(v) =>
                            void onUpdateLine(line.id, 'hardware', {
                              qty_per: typeof v === 'number' && v > 0 ? v : 1,
                            })
                          }
                        />
                      </td>

                      <td className={`${tdClass} ${dupTd}`}>
                        <InlineCell
                          value={line.color_hint}
                          placeholder="Gợi ý màu..."
                          emptyAsNull
                          onCommit={(v) =>
                            void onUpdateLine(line.id, 'hardware', {
                              color_hint: v === null ? null : String(v),
                            })
                          }
                        />
                      </td>

                      <td className={`${tdClass} ${dupTd}`}>
                        <InlineCell
                          value={line.notes}
                          placeholder="Ghi chú..."
                          emptyAsNull
                          onCommit={(v) =>
                            void onUpdateLine(line.id, 'hardware', {
                              notes: v === null ? null : String(v),
                            })
                          }
                        />
                      </td>

                      <td className={`${tdClass} ${dupTd}`}>
                        {renderColorChips(line.applicable_colors, () =>
                          onOpenScopePicker(line, 'hardware'),
                        )}
                      </td>

                      <td className={`${tdClass} ${dupTd}`}>
                        {renderAssemblyChips(line.applicable_assembly, () =>
                          onOpenScopePicker(line, 'hardware'),
                        )}
                      </td>

                      <td className={`${tdClass} text-center ${dupTd}`}>
                        <RowActionMenu
                          onDuplicate={() =>
                            onDuplicateLine(line.id, 'hardware')
                          }
                          onDelete={() => onDeleteLine(line.id, 'hardware')}
                        />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
